import type { IBrand, IMarket } from 'data/schema';
import { getSessionData, userSession } from 'state/session';
import { isValidSessionID } from 'utils/session';
import { tenantConfig } from 'utils/tenant';
import { user } from './user';

/**
 * AVS-4405 standalone dealer gate: the state SecureOutlet, the deferred route loaders and the dealer-session
 * helpers share. Not used by web components.
 */

interface IPassedGate {
  brand: string;
  market: string;
  dealerId: string;
}

// Module-level on purpose: it is read by route loaders, which run outside React.
let passedGate: IPassedGate | undefined;

/** Records that the gate passed for this brand, market and dealer (`''` for a non-dealer). */
export function markDealerGatePassed(brand: string, market: string, dealerId: string): void {
  passedGate = { brand, market, dealerId };
}

/**
 * True only for the brand/market the gate passed for AND the dealer still selected now, so clearing or changing
 * the stored selection closes the gate again. A non-dealer's `''` matches its empty selection.
 */
export function isDealerGatePassed(brand: string, market: string): boolean {
  return (
    passedGate !== undefined &&
    passedGate.brand === brand &&
    passedGate.market === market &&
    passedGate.dealerId === user.selectedDealerId
  );
}

export function resetDealerGate(): void {
  passedGate = undefined;
}

/**
 * Writes brand and market to the session (which is what makes tenant config load), waits for THAT brand and
 * market's config and resolves whether the market is inactive (`enabled === false`).
 * It never reads `inactive`, `isLoaded` or `configLoaded`: they cannot say that this key is loaded. A failed,
 * aborted or superseded load rejects; the caller maps it to DISABLED_MARKET.
 */
export async function activateDealerTenant(brand: IBrand, market: IMarket): Promise<boolean> {
  userSession().setValues({ brand, market });

  const config = await tenantConfig().whenLoaded(brand, market);

  return config.enabled === false;
}

/**
 * The switch target for a brand/market the window chose: the home path of that brand and market,
 * plus the live query string with `dealerId` dropped and every other parameter kept (`?test`,
 * `?dev`, `?log`, `?timer`, …), so the switch does not lose them.
 */
export function getDealerContextPath(brand: IBrand, market: IMarket, search: string): string {
  const params = new URLSearchParams(search);
  params.delete('dealerId');

  const query = params.toString();

  return `/${brand}/${market}/${query ? `?${query}` : ''}`;
}

async function fetchSessionRecord(sessionId: string) {
  try {
    return await getSessionData(sessionId);
  } catch {
    // The hint is optional: a failed read just means there is no hint (the request layer already reported it).
    return null;
  }
}

/**
 * The dealerId of a session's public record, used as a hint when a login lands on a session link.
 * Read-only: shares the session record cache, so a later `loadPublicSession` does not fetch again, and it
 * writes nothing to `userSession()`.
 */
export async function readSessionDealerHint(sessionId: string): Promise<string | undefined> {
  if (!isValidSessionID(sessionId)) {
    return undefined;
  }

  const record = await fetchSessionRecord(sessionId);

  return record?.dealerId || undefined;
}
